
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import {
  SingleChoiceSingleSelectionQuestion,
  MultiChoiceSingleSelectionQuestion,
  MultiChoiceMultiSelectionQuestion,
  RangeSingleSelectionQuestion
} from './recommended-questions';
import { CustomQuestion } from './custom-question';
import { PrivacyPolicy } from './privacy-policy';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "custom-questionnaire-panel-with-graphql"
    }}>{`Custom Questionnaire Panel with GraphQL`}</h1>
    <p>{`The SEEK `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/questionnaires/v2/panel"
      }}>{`Questionnaire Panel`}</a>{` is built on operations available through our GraphQL endpoint.
You can programmatically retrieve recommended questions, define custom questions and privacy policies, and create questionnaires.
This allows you to build your own custom user interface for questionnaires.`}</p>
    <h2 {...{
      "id": "before-you-begin"
    }}>{`Before you begin`}</h2>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Evaluate whether you can embed the SEEK-built `}<a parentName="p" {...{
            "href": "/use-cases/job-posting/questionnaires/v2/panel"
          }}>{`Questionnaire Panel`}</a>{`, which is a simpler alternative to creating your own implementation.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`SEEK recommends using a `}<a parentName="p" {...{
            "href": "/auth/browser-tokens"
          }}>{`browser token`}</a>{` to manage questionnaires directly from a hirer’s browser.
For the browser token to work, you will need to include the `}<inlineCode parentName="p">{`mutate:application-questionnaires query:application-library-question-suggestions query:application-questionnaires`}</inlineCode>{` scope in your request.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`You can also manage questionnaires from your backend using a `}<a parentName="p" {...{
            "href": "/auth/partner-tokens"
          }}>{`partner token`}</a>{`.`}</p>
      </li>
    </ul>
    <h2 {...{
      "id": "recommended-questions"
    }}>{`Recommended questions`}</h2>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/applicationLibraryQuestionSuggestions"
      }}><inlineCode parentName="a">{`applicationLibraryQuestionSuggestions`}</inlineCode></a>{` query returns a list of suggested SEEK library questions based on a partial `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/PositionProfile"
      }}><inlineCode parentName="a">{`PositionProfile`}</inlineCode></a>{`.
This can be used to interactively suggest application questions to a hirer while they’re posting a job ad.`}</p>
    <img alt="" data-scoobie-style="none" src={require('../../../../../mermaid/.eaa253ea4a18a9146afd620ddbd4e47302155043.mmd.svg')} title="question suggestions flow" />
    <p>{`You should invoke the query whenever a hirer updates one of these fields:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "/use-cases/job-posting/job-categories"
          }}>{`Job category`}</a></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "/use-cases/job-posting/locations"
          }}>{`Location`}</a></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "https://developer.seek.com/schema/#/named-type/PostPosition_PositionProfileInput/field/positionTitle"
          }}>{`Position title`}</a></p>
      </li>
    </ul>
    <p>{`While only the position’s title, category and location are required, providing `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/PositionFormattedDescriptionInput"
      }}>{`position descriptions`}</a>{` will increase suggestion relevance.`}</p>
    {/* We LOVATO ignore this as the suggestion IDs differ with each request. */}
    <pre><code parentName="pre" {...{
        "1": true,
        "5": true,
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query (\\n  $positionProfile: ApplicationLibraryQuestionSuggestions_PositionProfileInput!\\n  $schemeId: String!\\n  $first: Int\\n) {\\n  applicationLibraryQuestionSuggestions(\\n    schemeId: $schemeId\\n    positionProfile: $positionProfile\\n    first: $first\\n  ) {\\n    id {\\n      value\\n    }\\n    applicationLibraryQuestion {\\n      text\\n      id {\\n        value\\n      }\\n      preferenceSelection {\\n        typeCode\\n        message\\n      }\\n      responseTypeCode\\n      responseChoice {\\n        id {\\n          value\\n        }\\n        text\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":48,\"column\":1,\"offset\":2100},\"end\":{\"line\":81,\"column\":4,\"offset\":2659},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"schemeId\\\": \\\"seekAnzPublicTest\\\",\\n  \\\"first\\\": 3,\\n  \\\"positionProfile\\\": {\\n    \\\"jobCategories\\\": \\\"seekAnzPublicTest:jobCategory:seek:2EFstqFvP\\\",\\n    \\\"positionFormattedDescriptions\\\": {\\n      \\\"descriptionId\\\": \\\"AdvertisementDetails\\\",\\n      \\\"content\\\": \\\"A newly created role for an ambitious <b>junior</b> or <b>graduate</b> developer. Be part of a great team in the Bendigo area.\\\"\\n    },\\n    \\\"positionLocation\\\": \\\"seekAnzPublicTest:location:seek:2dkY4vZJF\\\",\\n    \\\"positionOrganizations\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n    \\\"positionTitle\\\": \\\"Software developer\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":83,\"column\":1,\"offset\":2661},\"end\":{\"line\":98,\"column\":4,\"offset\":3248},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":\"lovato-ignore-runnable-graphql-operation\",\"value\":\"{\\n  \\\"applicationLibraryQuestionSuggestions\\\": [\\n    {\\n      \\\"id\\\": {\\n        \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionSuggestion:seek:7iRhibfQhqAiPgA56UtdAzQMt6KuzNdiDbg6nG\\\"\\n      },\\n      \\\"applicationLibraryQuestion\\\": {\\n        \\\"text\\\": \\\"Which of the following statements best describes your right to work in Australia?\\\",\\n        \\\"id\\\": {\\n          \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestion:seek:DKVuWvRfaVZ81aCe\\\"\\n        },\\n        \\\"preferenceSelection\\\": {\\n          \\\"typeCode\\\": \\\"MultiChoice\\\",\\n          \\\"message\\\": \\\"I will accept any of these answers\\\"\\n        },\\n        \\\"responseTypeCode\\\": \\\"SingleSelect\\\",\\n        \\\"responseChoice\\\": [\\n          {\\n            \\\"id\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1o4QTKfm\\\"\\n            },\\n            \\\"text\\\": \\\"I'm an Australian citizen\\\"\\n          },\\n          {\\n            \\\"id\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1o4QTKfn\\\"\\n            },\\n            \\\"text\\\": \\\"I'm a permanent resident and/or NZ citizen\\\"\\n          },\\n          {\\n            \\\"id\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1o4QTKfo\\\"\\n            },\\n            \\\"text\\\": \\\"I have a family/partner visa with no restrictions\\\"\\n          },\\n          {\\n            \\\"id\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1o4QTKfp\\\"\\n            },\\n            \\\"text\\\": \\\"I have a graduate temporary work visa\\\"\\n          },\\n          {\\n            \\\"id\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1o4QTKfq\\\"\\n            },\\n            \\\"text\\\": \\\"I have a holiday temporary work visa\\\"\\n          },\\n          {\\n            \\\"id\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1o4QTKfr\\\"\\n            },\\n            \\\"text\\\": \\\"I have a temporary visa with restrictions on work location (e.g. skilled regional visa 489)\\\"\\n          },\\n          {\\n            \\\"id\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1o4QTKfs\\\"\\n            },\\n            \\\"text\\\": \\\"I have a temporary protection or safe haven enterprise work visa\\\"\\n          },\\n          {\\n            \\\"id\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1oB3u67i\\\"\\n            },\\n            \\\"text\\\": \\\"I have a temporary visa with no restrictions (e.g. doctoral student)\\\"\\n          },\\n          {\\n            \\\"id\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1o4QTKft\\\"\\n            },\\n            \\\"text\\\": \\\"I have a temporary visa with restrictions on work hours (e.g. student visa, retirement visa)\\\"\\n          },\\n          {\\n            \\\"id\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1oB5MQ98\\\"\\n            },\\n            \\\"text\\\": \\\"I have a temporary visa with restrictions on industry (e.g. temporary activity visa 408)\\\"\\n          },\\n          {\\n            \\\"id\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1o4QTKfu\\\"\\n            },\\n            \\\"text\\\": \\\"I require sponsorship to work for a new employer (e.g. 482, 457)\\\"\\n          }\\n        ]\\n      }\\n    },\\n    {\\n      \\\"id\\\": {\\n        \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionSuggestion:seek:DcEDpNFbsf4nnZaGjoF8f2ptBtJ24ieg25MbHH1ZgQ\\\"\\n      },\\n      \\\"applicationLibraryQuestion\\\": {\\n        \\\"text\\\": \\\"How many years' experience do you have as a software developer?\\\",\\n        \\\"id\\\": {\\n          \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestion:seek:Q8cuGUN6cQ9mtTF4oR47\\\"\\n        },\\n        \\\"preferenceSelection\\\": {\\n          \\\"typeCode\\\": \\\"SingleChoice\\\",\\n          \\\"message\\\": \\\"I will accept above and including\\\"\\n        },\\n        \\\"responseTypeCode\\\": \\\"SingleSelect\\\",\\n        \\\"responseChoice\\\": [\\n          {\\n            \\\"id\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:6mba8Nq4qg49UKTP8HvUPj3r3pm1JF\\\"\\n            },\\n            \\\"text\\\": \\\"No experience\\\"\\n          },\\n          {\\n            \\\"id\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:6mba8Nq4qg49UKTP8HvUPj3r3pm1JG\\\"\\n            },\\n            \\\"text\\\": \\\"Less than 1 year\\\"\\n          },\\n          {\\n            \\\"id\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:6mba8Nq4qg49UKTP8HvUPj3r3pm1NX\\\"\\n            },\\n            \\\"text\\\": \\\"1 year\\\"\\n          },\\n          {\\n            \\\"id\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:6mba8Nq4qg49UKTP8HvUPj3r3pm1NY\\\"\\n            },\\n            \\\"text\\\": \\\"2 years\\\"\\n          },\\n          {\\n            \\\"id\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:6mba8Nq4qg49UKTP8HvUPj3r3pm1NZ\\\"\\n            },\\n            \\\"text\\\": \\\"3 years\\\"\\n          },\\n          {\\n            \\\"id\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:6mba8Nq4qg49UKTP8HvUPj3r3pm1Na\\\"\\n            },\\n            \\\"text\\\": \\\"4 years\\\"\\n          },\\n          {\\n            \\\"id\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:6mba8Nq4qg49UKTP8HvUPj3r3pm1Nb\\\"\\n            },\\n            \\\"text\\\": \\\"5 years\\\"\\n          },\\n          {\\n            \\\"id\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:6mba8Nq4qg49UKTP8HvUPj3r3pm1Nc\\\"\\n            },\\n            \\\"text\\\": \\\"More than 5 years\\\"\\n          }\\n        ]\\n      }\\n    },\\n    {\\n      \\\"id\\\": {\\n        \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionSuggestion:seek:3f3jmcsJXp1QdEMroPwVNXUTMDmTAuNvPEahhDCBC\\\"\\n      },\\n      \\\"applicationLibraryQuestion\\\": {\\n        \\\"text\\\": \\\"Have you worked in a role which requires JavaScript development experience?\\\",\\n        \\\"id\\\": {\\n          \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestion:seek:5sdzVTGoNg3fVCEK1WZ\\\"\\n        },\\n        \\\"preferenceSelection\\\": {\\n          \\\"typeCode\\\": \\\"SingleChoice\\\",\\n          \\\"message\\\": \\\"I will only accept this answer\\\"\\n        },\\n        \\\"responseTypeCode\\\": \\\"SingleSelect\\\",\\n        \\\"responseChoice\\\": [\\n          {\\n            \\\"id\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:6mba8Nq4qfwhxEFdA34962VrVqtEtP\\\"\\n            },\\n            \\\"text\\\": \\\"Yes\\\"\\n          },\\n          {\\n            \\\"id\\\": {\\n              \\\"value\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:6mba8Nq4qfwhxEFdA34962VrVqtEtQ\\\"\\n            },\\n            \\\"text\\\": \\\"No\\\"\\n          }\\n        ]\\n      }\\n    }\\n  ]\\n}\",\"position\":{\"start\":{\"line\":100,\"column\":1,\"offset\":3250},\"end\":{\"line\":285,\"column\":4,\"offset\":10052},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "(\\n": true,
        "": true,
        "$positionProfile:": true,
        "ApplicationLibraryQuestionSuggestions_PositionProfileInput!\\n": true,
        "$schemeId:": true,
        "String!\\n": true,
        "$first:": true,
        "Int\\n)": true,
        "{\\n": true,
        "applicationLibraryQuestionSuggestions(\\n": true,
        "schemeId:": true,
        "$schemeId\\n": true,
        "positionProfile:": true,
        "$positionProfile\\n": true,
        "first:": true,
        "$first\\n": true,
        ")": true,
        "id": true,
        "value\\n": true,
        "}\\n": true,
        "applicationLibraryQuestion": true,
        "text\\n": true,
        "preferenceSelection": true,
        "typeCode\\n": true,
        "message\\n": true,
        "responseTypeCode\\n": true,
        "responseChoice": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":48,\"column\":1,\"offset\":2100},\"end\":{\"line\":81,\"column\":4,\"offset\":2659},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"schemeId\\\":": true,
        "\\\"seekAnzPublicTest\\\",\\n": true,
        "\\\"first\\\":": true,
        "3,\\n": true,
        "\\\"positionProfile\\\":": true,
        "\\\"jobCategories\\\":": true,
        "\\\"seekAnzPublicTest:jobCategory:seek:2EFstqFvP\\\",\\n": true,
        "\\\"positionFormattedDescriptions\\\":": true,
        "\\\"descriptionId\\\":": true,
        "\\\"AdvertisementDetails\\\",\\n": true,
        "\\\"content\\\":": true,
        "\\\"A": true,
        "newly": true,
        "created": true,
        "role": true,
        "for": true,
        "an": true,
        "ambitious": true,
        "<b>junior</b>": true,
        "or": true,
        "<b>graduate</b>": true,
        "developer.": true,
        "Be": true,
        "part": true,
        "of": true,
        "a": true,
        "great": true,
        "team": true,
        "in": true,
        "the": true,
        "Bendigo": true,
        "area.\\\"\\n": true,
        "},\\n": true,
        "\\\"positionLocation\\\":": true,
        "\\\"seekAnzPublicTest:location:seek:2dkY4vZJF\\\",\\n": true,
        "\\\"positionOrganizations\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n": true,
        "\\\"positionTitle\\\":": true,
        "\\\"Software": true,
        "developer\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":83,\"column\":1,\"offset\":2661},\"end\":{\"line\":98,\"column\":4,\"offset\":3248},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":\"lovato-ignore-runnable-graphql-operation\",\"value\":\"{\\n": true,
        "\\\"applicationLibraryQuestionSuggestions\\\":": true,
        "[\\n": true,
        "\\\"id\\\":": true,
        "\\\"value\\\":": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionSuggestion:seek:7iRhibfQhqAiPgA56UtdAzQMt6KuzNdiDbg6nG\\\"\\n": true,
        "\\\"applicationLibraryQuestion\\\":": true,
        "\\\"text\\\":": true,
        "\\\"Which": true,
        "following": true,
        "statements": true,
        "best": true,
        "describes": true,
        "your": true,
        "right": true,
        "to": true,
        "work": true,
        "Australia?\\\",\\n": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestion:seek:DKVuWvRfaVZ81aCe\\\"\\n": true,
        "\\\"preferenceSelection\\\":": true,
        "\\\"typeCode\\\":": true,
        "\\\"MultiChoice\\\",\\n": true,
        "\\\"message\\\":": true,
        "\\\"I": true,
        "will": true,
        "accept": true,
        "any": true,
        "these": true,
        "answers\\\"\\n": true,
        "\\\"responseTypeCode\\\":": true,
        "\\\"SingleSelect\\\",\\n": true,
        "\\\"responseChoice\\\":": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1o4QTKfm\\\"\\n": true,
        "\\\"I'm": true,
        "Australian": true,
        "citizen\\\"\\n": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1o4QTKfn\\\"\\n": true,
        "permanent": true,
        "resident": true,
        "and/or": true,
        "NZ": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1o4QTKfo\\\"\\n": true,
        "have": true,
        "family/partner": true,
        "visa": true,
        "with": true,
        "no": true,
        "restrictions\\\"\\n": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1o4QTKfp\\\"\\n": true,
        "graduate": true,
        "temporary": true,
        "visa\\\"\\n": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1o4QTKfq\\\"\\n": true,
        "holiday": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1o4QTKfr\\\"\\n": true,
        "restrictions": true,
        "on": true,
        "location": true,
        "(e.g.": true,
        "skilled": true,
        "regional": true,
        "489)\\\"\\n": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1o4QTKfs\\\"\\n": true,
        "protection": true,
        "safe": true,
        "haven": true,
        "enterprise": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1oB3u67i\\\"\\n": true,
        "doctoral": true,
        "student)\\\"\\n": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1o4QTKft\\\"\\n": true,
        "hours": true,
        "student": true,
        "visa,": true,
        "retirement": true,
        "visa)\\\"\\n": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1oB5MQ98\\\"\\n": true,
        "industry": true,
        "activity": true,
        "408)\\\"\\n": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:GZRExMtKn3DC21JvHwS1o4QTKfu\\\"\\n": true,
        "require": true,
        "sponsorship": true,
        "new": true,
        "employer": true,
        "482,": true,
        "457)\\\"\\n": true,
        "]\\n": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionSuggestion:seek:DcEDpNFbsf4nnZaGjoF8f2ptBtJ24ieg25MbHH1ZgQ\\\"\\n": true,
        "\\\"How": true,
        "many": true,
        "years'": true,
        "experience": true,
        "do": true,
        "you": true,
        "as": true,
        "software": true,
        "developer?\\\",\\n": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestion:seek:Q8cuGUN6cQ9mtTF4oR47\\\"\\n": true,
        "\\\"SingleChoice\\\",\\n": true,
        "above": true,
        "and": true,
        "including\\\"\\n": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:6mba8Nq4qg49UKTP8HvUPj3r3pm1JF\\\"\\n": true,
        "\\\"No": true,
        "experience\\\"\\n": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:6mba8Nq4qg49UKTP8HvUPj3r3pm1JG\\\"\\n": true,
        "\\\"Less": true,
        "than": true,
        "year\\\"\\n": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:6mba8Nq4qg49UKTP8HvUPj3r3pm1NX\\\"\\n": true,
        "\\\"1": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:6mba8Nq4qg49UKTP8HvUPj3r3pm1NY\\\"\\n": true,
        "\\\"2": true,
        "years\\\"\\n": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:6mba8Nq4qg49UKTP8HvUPj3r3pm1NZ\\\"\\n": true,
        "\\\"3": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:6mba8Nq4qg49UKTP8HvUPj3r3pm1Na\\\"\\n": true,
        "\\\"4": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:6mba8Nq4qg49UKTP8HvUPj3r3pm1Nb\\\"\\n": true,
        "\\\"5": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:6mba8Nq4qg49UKTP8HvUPj3r3pm1Nc\\\"\\n": true,
        "\\\"More": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionSuggestion:seek:3f3jmcsJXp1QdEMroPwVNXUTMDmTAuNvPEahhDCBC\\\"\\n": true,
        "\\\"Have": true,
        "worked": true,
        "which": true,
        "requires": true,
        "JavaScript": true,
        "development": true,
        "experience?\\\",\\n": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestion:seek:5sdzVTGoNg3fVCEK1WZ\\\"\\n": true,
        "only": true,
        "this": true,
        "answer\\\"\\n": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:6mba8Nq4qfwhxEFdA34962VrVqtEtP\\\"\\n": true,
        "\\\"Yes\\\"\\n": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:6mba8Nq4qfwhxEFdA34962VrVqtEtQ\\\"\\n": true,
        "\\\"No\\\"\\n": true,
        "]\\n}\",\"position\":{\"start\":{\"line\":100,\"column\":1,\"offset\":3250},\"end\":{\"line\":285,\"column\":4,\"offset\":10052},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query (
  $positionProfile: ApplicationLibraryQuestionSuggestions_PositionProfileInput!
  $schemeId: String!
  $first: Int
) {
  applicationLibraryQuestionSuggestions(
    schemeId: $schemeId
    positionProfile: $positionProfile
    first: $first
  ) {
    id {
      value
    }
    applicationLibraryQuestion {
      text
      id {
        value
      }
      preferenceSelection {
        typeCode
        message
      }
      responseTypeCode
      responseChoice {
        id {
          value
        }
        text
      }
    }
  }
}
`}</code></pre>
    <p>{`Each suggested `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplicationLibraryQuestion"
      }}><inlineCode parentName="a">{`applicationLibraryQuestion`}</inlineCode></a>{` contains the details of a question from SEEK’s library.
A hirer may select up to 8 suggested questions for inclusion in their questionnaire.`}</p>
    <p>{`You should present the hirer with an appropriate input control to record their preferred response choices to each selected question,
which drives `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/questionnaires#preferred-choices--scoring"
      }}>{`scoring`}</a>{` of questionnaire submissions.
The `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplicationLibraryQuestion/field/preferenceSelection"
      }}><inlineCode parentName="a">{`preferenceSelection`}</inlineCode></a>{` field describes how you should present the response choice selection.`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`The available `}<a parentName="p" {...{
            "href": "https://developer.seek.com/schema/#/named-type/ApplicationLibraryQuestionPreferenceSelection/field/typeCode"
          }}><inlineCode parentName="a">{`typeCode`}</inlineCode></a>{`s and corresponding sample implementations are listed below.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The `}<a parentName="p" {...{
            "href": "https://developer.seek.com/schema/#/named-type/ApplicationLibraryQuestionPreferenceSelection/field/message"
          }}><inlineCode parentName="a">{`message`}</inlineCode></a>{` is a description that should be displayed to the hirer to prompt their preference selection.`}</p>
      </li>
    </ul>
    <p>{`The following samples provide a visual representation of how you should present the hirer view for each scenario.
The accompanying candidate view approximates how SEEK will present the question on our application form per the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplicationLibraryQuestion/field/responseTypeCode"
      }}><inlineCode parentName="a">{`responseTypeCode`}</inlineCode></a>{`;
it exists for illustrative purposes only and does not need to be shown within your software.`}</p>

    <h3 {...{
      "id": "single-choice"
    }}>{`Single choice`}</h3>
    <p><inlineCode parentName="p">{`SingleChoice`}</inlineCode>{` indicates that the hirer can select a single preferred response choice.
A radio group or dropdown should be used.`}</p>
    <p>{`For a question with a `}<inlineCode parentName="p">{`SingleSelect`}</inlineCode>{` candidate response:`}</p>
    <SingleChoiceSingleSelectionQuestion mdxType="SingleChoiceSingleSelectionQuestion" />
    <h3 {...{
      "id": "multi-choice"
    }}>{`Multi choice`}</h3>
    <p><inlineCode parentName="p">{`MultiChoice`}</inlineCode>{` indicates that the hirer can select multiple preferred response choices.
A checkbox group should be used.`}</p>
    <p>{`For a question with a `}<inlineCode parentName="p">{`SingleSelect`}</inlineCode>{` candidate response:`}</p>
    <MultiChoiceSingleSelectionQuestion mdxType="MultiChoiceSingleSelectionQuestion" />
    <p>{`For a question with a `}<inlineCode parentName="p">{`MultiSelect`}</inlineCode>{` candidate response:`}</p>
    <MultiChoiceMultiSelectionQuestion mdxType="MultiChoiceMultiSelectionQuestion" />
    <h3 {...{
      "id": "range"
    }}>{`Range`}</h3>
    <p><inlineCode parentName="p">{`Range`}</inlineCode>{` indicates that the hirer can select two response choices representing the minimum and maximum preferred choices.
A set of two dropdowns should be used.`}</p>
    <p>{`For a question with a `}<inlineCode parentName="p">{`SingleSelect`}</inlineCode>{` candidate response:`}</p>
    <RangeSingleSelectionQuestion mdxType="RangeSingleSelectionQuestion" />
    <p>{`The minimum dropdown should implement the following rules:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`The last option should be disabled`}</p>
      </li>
    </ol>
    <p>{`The maximum dropdown should implement the following rules:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`The first option should be disabled`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`The selected minimum and preceding options should be disabled`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`The selection should be reset if the minimum is greater than or equal to it`}</p>
      </li>
    </ol>
    <h2 {...{
      "id": "custom-questions"
    }}>{`Custom questions`}</h2>
    <p>{`If a hirer is unable to find a relevant question from SEEK’s library,
they may wish to provide their own question.
A hirer may create up to 3 custom questions for inclusion in their questionnaire.`}</p>
    <p>{`We recommend supporting the same question types as the SEEK-built `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/questionnaires/v2/panel"
      }}>{`Questionnaire Panel`}</a>{`:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Free text`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Yes/no`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Single select`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Multi select`}</p>
      </li>
    </ol>
    <p>{`However, you may choose to omit complex selection types if there is limited demand from your hirers.`}</p>

    <CustomQuestion mdxType="CustomQuestion" />
    <p>{`The above sample generates the corresponding `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplicationQuestionnaireComponentInput"
      }}><inlineCode parentName="a">{`ApplicationQuestionnaireComponentInput`}</inlineCode></a>{` to supply to `}<a parentName="p" {...{
        "href": "#questionnaire-creation"
      }}>{`questionnaire creation`}</a>{`.`}</p>
    <h2 {...{
      "id": "privacy-policy"
    }}>{`Privacy policy`}</h2>
    <p>{`If your software stores a link to the hirer’s privacy policy,
you can present a checkbox that requires candidates to accept the privacy policy as part of the application form.
The description and URL of the privacy policy should be populated by your backend,
as the hirer shouldn’t need to tailor these details per job ad.`}</p>

    <PrivacyPolicy mdxType="PrivacyPolicy" />
    <p>{`The above sample generates the corresponding `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplicationQuestionnaireComponentInput"
      }}><inlineCode parentName="a">{`ApplicationQuestionnaireComponentInput`}</inlineCode></a>{` to supply to `}<a parentName="p" {...{
        "href": "#questionnaire-creation"
      }}>{`questionnaire creation`}</a>{`.`}</p>
    <h2 {...{
      "id": "questionnaire-creation"
    }}>{`Questionnaire creation`}</h2>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/mutation/createApplicationQuestionnaire"
      }}><inlineCode parentName="a">{`createApplicationQuestionnaire`}</inlineCode></a>{` mutation creates a new questionnaire.
You can include SEEK library questions from the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/applicationLibraryQuestionSuggestions"
      }}><inlineCode parentName="a">{`applicationLibraryQuestionSuggestions`}</inlineCode></a>{` query, custom questions, and a privacy consent component.`}</p>
    <h3 {...{
      "id": "for-recommended-questions"
    }}>{`For recommended questions`}</h3>
    <p>{`If a library question was suggested via the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/applicationLibraryQuestionSuggestions"
      }}><inlineCode parentName="a">{`applicationLibraryQuestionSuggestions`}</inlineCode></a>{` query,
you should supply its `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplicationLibraryQuestionSuggestion/field/id"
      }}><inlineCode parentName="a">{`ApplicationLibraryQuestionSuggestion.id`}</inlineCode></a>{` as input to the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/mutation/createApplicationQuestionnaire"
      }}><inlineCode parentName="a">{`createApplicationQuestionnaire`}</inlineCode></a>{` mutation.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation ($input: CreateApplicationQuestionnaireInput!) {\\n  createApplicationQuestionnaire(input: $input) {\\n    applicationQuestionnaire {\\n      id {\\n        value\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":394,\"column\":1,\"offset\":14213},\"end\":{\"line\":404,\"column\":4,\"offset\":14411},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"input\\\": {\\n    \\\"applicationQuestionnaire\\\": {\\n      \\\"hirerId\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n      \\\"components\\\": [\\n        {\\n          \\\"componentTypeCode\\\": \\\"Question\\\",\\n          \\\"libraryQuestion\\\": {\\n            \\\"componentTypeCode\\\": \\\"Question\\\",\\n            \\\"id\\\": \\\"seekAnzPublicTest:applicationLibraryQuestionSuggestion:seek:2PPe5xSH2Sw1bQR1HKKzx9McxyvsGjSWQW6t7\\\",\\n            \\\"selectedResponseChoice\\\": [\\n              \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:4LqV9pD5uNXwgUe42maXd8NBeo\\\"\\n            ]\\n          }\\n        },\\n        {\\n          \\\"componentTypeCode\\\": \\\"Question\\\",\\n          \\\"libraryQuestion\\\": {\\n            \\\"componentTypeCode\\\": \\\"Question\\\",\\n            \\\"id\\\": \\\"seekAnzPublicTest:applicationLibraryQuestion:seek:3YVmYzzxFKZ4ert\\\",\\n            \\\"selectedResponseChoice\\\": [\\n              \\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:4LqV9pD5uNXwgUe42maXd8NBeo\\\"\\n            ]\\n          }\\n        }\\n      ]\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":406,\"column\":1,\"offset\":14413},\"end\":{\"line\":436,\"column\":4,\"offset\":15395},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation": true,
        "($input:": true,
        "CreateApplicationQuestionnaireInput!)": true,
        "{\\n": true,
        "": true,
        "createApplicationQuestionnaire(input:": true,
        "$input)": true,
        "applicationQuestionnaire": true,
        "id": true,
        "value\\n": true,
        "}\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":394,\"column\":1,\"offset\":14213},\"end\":{\"line\":404,\"column\":4,\"offset\":14411},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "\\\"applicationQuestionnaire\\\":": true,
        "\\\"hirerId\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n": true,
        "\\\"components\\\":": true,
        "[\\n": true,
        "\\\"componentTypeCode\\\":": true,
        "\\\"Question\\\",\\n": true,
        "\\\"libraryQuestion\\\":": true,
        "\\\"id\\\":": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionSuggestion:seek:2PPe5xSH2Sw1bQR1HKKzx9McxyvsGjSWQW6t7\\\",\\n": true,
        "\\\"selectedResponseChoice\\\":": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestionChoice:seek:4LqV9pD5uNXwgUe42maXd8NBeo\\\"\\n": true,
        "]\\n": true,
        "},\\n": true,
        "\\\"seekAnzPublicTest:applicationLibraryQuestion:seek:3YVmYzzxFKZ4ert\\\",\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":406,\"column\":1,\"offset\":14413},\"end\":{\"line\":436,\"column\":4,\"offset\":15395},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`mutation ($input: CreateApplicationQuestionnaireInput!) {
  createApplicationQuestionnaire(input: $input) {
    applicationQuestionnaire {
      id {
        value
      }
    }
  }
}
`}</code></pre>
    <h3 {...{
      "id": "for-custom-questions-and-privacy-policies"
    }}>{`For custom questions and privacy policies`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation ($input: CreateApplicationQuestionnaireInput!) {\\n  createApplicationQuestionnaire(input: $input) {\\n    applicationQuestionnaire {\\n      id {\\n        value\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":440,\"column\":1,\"offset\":15444},\"end\":{\"line\":450,\"column\":4,\"offset\":15642},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"input\\\": {\\n    \\\"applicationQuestionnaire\\\": {\\n      \\\"hirerId\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n      \\\"components\\\": [\\n        {\\n          \\\"componentTypeCode\\\": \\\"Question\\\",\\n          \\\"question\\\": {\\n            \\\"componentTypeCode\\\": \\\"Question\\\",\\n            \\\"value\\\": \\\"question1\\\",\\n            \\\"questionHtml\\\": \\\"Tell us about your relevant mock data experience\\\",\\n            \\\"responseTypeCode\\\": \\\"FreeText\\\"\\n          }\\n        },\\n        {\\n          \\\"componentTypeCode\\\": \\\"PrivacyConsent\\\",\\n          \\\"privacyConsent\\\": {\\n            \\\"componentTypeCode\\\": \\\"PrivacyConsent\\\",\\n            \\\"value\\\": \\\"consent1\\\",\\n            \\\"descriptionHtml\\\": \\\"Do you agree to our privacy policy?\\\",\\n            \\\"privacyPolicyUrl\\\": {\\n              \\\"url\\\": \\\"https://www.seek.com.au/privacy/\\\"\\n            }\\n          }\\n        }\\n      ]\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":452,\"column\":1,\"offset\":15644},\"end\":{\"line\":482,\"column\":4,\"offset\":16482},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"mutation": true,
        "($input:": true,
        "CreateApplicationQuestionnaireInput!)": true,
        "{\\n": true,
        "": true,
        "createApplicationQuestionnaire(input:": true,
        "$input)": true,
        "applicationQuestionnaire": true,
        "id": true,
        "value\\n": true,
        "}\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":440,\"column\":1,\"offset\":15444},\"end\":{\"line\":450,\"column\":4,\"offset\":15642},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "\\\"applicationQuestionnaire\\\":": true,
        "\\\"hirerId\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n": true,
        "\\\"components\\\":": true,
        "[\\n": true,
        "\\\"componentTypeCode\\\":": true,
        "\\\"Question\\\",\\n": true,
        "\\\"question\\\":": true,
        "\\\"value\\\":": true,
        "\\\"question1\\\",\\n": true,
        "\\\"questionHtml\\\":": true,
        "\\\"Tell": true,
        "us": true,
        "about": true,
        "your": true,
        "relevant": true,
        "mock": true,
        "data": true,
        "experience\\\",\\n": true,
        "\\\"responseTypeCode\\\":": true,
        "\\\"FreeText\\\"\\n": true,
        "},\\n": true,
        "\\\"PrivacyConsent\\\",\\n": true,
        "\\\"privacyConsent\\\":": true,
        "\\\"consent1\\\",\\n": true,
        "\\\"descriptionHtml\\\":": true,
        "\\\"Do": true,
        "you": true,
        "agree": true,
        "to": true,
        "our": true,
        "privacy": true,
        "policy?\\\",\\n": true,
        "\\\"privacyPolicyUrl\\\":": true,
        "\\\"url\\\":": true,
        "\\\"https://www.seek.com.au/privacy/\\\"\\n": true,
        "]\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":452,\"column\":1,\"offset\":15644},\"end\":{\"line\":482,\"column\":4,\"offset\":16482},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`mutation ($input: CreateApplicationQuestionnaireInput!) {
  createApplicationQuestionnaire(input: $input) {
    applicationQuestionnaire {
      id {
        value
      }
    }
  }
}
`}</code></pre>
    <h2 {...{
      "id": "questionnaire-retrieval"
    }}>{`Questionnaire retrieval`}</h2>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/applicationQuestionnaire"
      }}><inlineCode parentName="a">{`applicationQuestionnaire`}</inlineCode>{` query`}</a>{` retrieves a previously created questionnaire by its identifier.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($id: String!) {\\n  applicationQuestionnaire(id: $id) {\\n    id {\\n      value\\n    }\\n    components {\\n      id {\\n        value\\n      }\\n      componentTypeCode\\n      ... on ApplicationQuestion {\\n        questionHtml\\n        responseTypeCode\\n        value\\n        responseChoice {\\n          text\\n          value\\n        }\\n      }\\n      ... on ApplicationPrivacyConsent {\\n        descriptionHtml\\n        privacyPolicyUrl {\\n          url\\n        }\\n      }\\n    }\\n  }\\n}\",\"position\":{\"start\":{\"line\":488,\"column\":1,\"offset\":16616},\"end\":{\"line\":517,\"column\":4,\"offset\":17097},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"id\\\": \\\"seekAnzPublicTest:applicationQuestionnaire:rrv2:SqFmkV8S2dMipyqbHjD9Mr\\\"\\n}\",\"position\":{\"start\":{\"line\":519,\"column\":1,\"offset\":17099},\"end\":{\"line\":523,\"column\":4,\"offset\":17195},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"applicationQuestionnaire\\\": {\\n    \\\"id\\\": {\\n      \\\"value\\\": \\\"seekAnzPublicTest:applicationQuestionnaire:rrv2:SqFmkV8S2dMipyqbHjD9Mr\\\"\\n    },\\n    \\\"components\\\": [\\n      {\\n        \\\"id\\\": {\\n          \\\"value\\\": \\\"seekAnzPublicTest:applicationQuestionnaireComponent:rrv2:yn5Hrb4iL9MCGkZNLVe4BCe8dro9Sw\\\"\\n        },\\n        \\\"componentTypeCode\\\": \\\"Question\\\",\\n        \\\"questionHtml\\\": \\\"Which of the following statements best describes your right to work in Australia?\\\",\\n        \\\"responseTypeCode\\\": \\\"SingleSelect\\\",\\n        \\\"value\\\": null,\\n        \\\"responseChoice\\\": [\\n          {\\n            \\\"text\\\": \\\"I'm an Australian citizen\\\",\\n            \\\"value\\\": null\\n          },\\n          {\\n            \\\"text\\\": \\\"I'm a permanent resident and/or NZ citizen\\\",\\n            \\\"value\\\": null\\n          },\\n          {\\n            \\\"text\\\": \\\"I have a family/partner visa with no restrictions\\\",\\n            \\\"value\\\": null\\n          },\\n          {\\n            \\\"text\\\": \\\"I have a graduate temporary work visa\\\",\\n            \\\"value\\\": null\\n          },\\n          {\\n            \\\"text\\\": \\\"I have a holiday temporary work visa\\\",\\n            \\\"value\\\": null\\n          },\\n          {\\n            \\\"text\\\": \\\"I have a temporary visa with restrictions on work location (e.g. skilled regional visa 489)\\\",\\n            \\\"value\\\": null\\n          },\\n          {\\n            \\\"text\\\": \\\"I have a temporary protection or safe haven enterprise work visa\\\",\\n            \\\"value\\\": null\\n          },\\n          {\\n            \\\"text\\\": \\\"I have a temporary visa with no restrictions (e.g. doctoral student)\\\",\\n            \\\"value\\\": null\\n          },\\n          {\\n            \\\"text\\\": \\\"I have a temporary visa with restrictions on work hours (e.g. student visa, retirement visa\\\",\\n            \\\"value\\\": null\\n          },\\n          {\\n            \\\"text\\\": \\\"I require sponsorship to work for a new employer (e.g. 482, 457)\\\",\\n            \\\"value\\\": null\\n          }\\n        ]\\n      },\\n      {\\n        \\\"id\\\": {\\n          \\\"value\\\": \\\"seekAnzPublicTest:applicationQuestionnaireComponent:rrv2:yn5Hrb4iL9MCGkZNLVe4BCedX2sdyZ\\\"\\n        },\\n        \\\"componentTypeCode\\\": \\\"Question\\\",\\n        \\\"questionHtml\\\": \\\"Do you own or have regular access to a car?\\\",\\n        \\\"responseTypeCode\\\": \\\"SingleSelect\\\",\\n        \\\"value\\\": null,\\n        \\\"responseChoice\\\": [\\n          {\\n            \\\"text\\\": \\\"Yes\\\",\\n            \\\"value\\\": null\\n          },\\n          {\\n            \\\"text\\\": \\\"No\\\",\\n            \\\"value\\\": null\\n          }\\n        ]\\n      },\\n      {\\n        \\\"id\\\": {\\n          \\\"value\\\": \\\"seekAnzPublicTest:applicationQuestionnaireComponent:rrv2:yn5Hrb4iL9MCGkZNLVe4BCf8QCx8WB\\\"\\n        },\\n        \\\"componentTypeCode\\\": \\\"Question\\\",\\n        \\\"questionHtml\\\": \\\"What do you like most about GraphQL?\\\",\\n        \\\"responseTypeCode\\\": \\\"FreeText\\\",\\n        \\\"value\\\": null,\\n        \\\"responseChoice\\\": []\\n      },\\n      {\\n        \\\"id\\\": {\\n          \\\"value\\\": \\\"seekAnzPublicTest:applicationQuestionnaireComponent:rrv2:yn5Hrb4iL9MCGkZNLVe4BCfdHP2d2o\\\"\\n        },\\n        \\\"componentTypeCode\\\": \\\"PrivacyConsent\\\",\\n        \\\"descriptionHtml\\\": \\\"Do you agree with our privacy policy?\\\",\\n        \\\"privacyPolicyUrl\\\": {\\n          \\\"url\\\": \\\"https://www.seek.com.au/privacy/\\\"\\n        }\\n      }\\n    ]\\n  }\\n}\",\"position\":{\"start\":{\"line\":525,\"column\":1,\"offset\":17197},\"end\":{\"line\":625,\"column\":4,\"offset\":20338},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($id:": true,
        "String!)": true,
        "{\\n": true,
        "": true,
        "applicationQuestionnaire(id:": true,
        "$id)": true,
        "id": true,
        "value\\n": true,
        "}\\n": true,
        "components": true,
        "componentTypeCode\\n": true,
        "...": true,
        "on": true,
        "ApplicationQuestion": true,
        "questionHtml\\n": true,
        "responseTypeCode\\n": true,
        "responseChoice": true,
        "text\\n": true,
        "ApplicationPrivacyConsent": true,
        "descriptionHtml\\n": true,
        "privacyPolicyUrl": true,
        "url\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":488,\"column\":1,\"offset\":16616},\"end\":{\"line\":517,\"column\":4,\"offset\":17097},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"id\\\":": true,
        "\\\"seekAnzPublicTest:applicationQuestionnaire:rrv2:SqFmkV8S2dMipyqbHjD9Mr\\\"\\n}\",\"position\":{\"start\":{\"line\":519,\"column\":1,\"offset\":17099},\"end\":{\"line\":523,\"column\":4,\"offset\":17195},\"indent\":[1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"applicationQuestionnaire\\\":": true,
        "\\\"value\\\":": true,
        "\\\"seekAnzPublicTest:applicationQuestionnaire:rrv2:SqFmkV8S2dMipyqbHjD9Mr\\\"\\n": true,
        "},\\n": true,
        "\\\"components\\\":": true,
        "[\\n": true,
        "\\\"seekAnzPublicTest:applicationQuestionnaireComponent:rrv2:yn5Hrb4iL9MCGkZNLVe4BCe8dro9Sw\\\"\\n": true,
        "\\\"componentTypeCode\\\":": true,
        "\\\"Question\\\",\\n": true,
        "\\\"questionHtml\\\":": true,
        "\\\"Which": true,
        "of": true,
        "the": true,
        "following": true,
        "statements": true,
        "best": true,
        "describes": true,
        "your": true,
        "right": true,
        "to": true,
        "work": true,
        "in": true,
        "Australia?\\\",\\n": true,
        "\\\"responseTypeCode\\\":": true,
        "\\\"SingleSelect\\\",\\n": true,
        "null,\\n": true,
        "\\\"responseChoice\\\":": true,
        "\\\"text\\\":": true,
        "\\\"I'm": true,
        "an": true,
        "Australian": true,
        "citizen\\\",\\n": true,
        "null\\n": true,
        "a": true,
        "permanent": true,
        "resident": true,
        "and/or": true,
        "NZ": true,
        "\\\"I": true,
        "have": true,
        "family/partner": true,
        "visa": true,
        "with": true,
        "no": true,
        "restrictions\\\",\\n": true,
        "graduate": true,
        "temporary": true,
        "visa\\\",\\n": true,
        "holiday": true,
        "restrictions": true,
        "location": true,
        "(e.g.": true,
        "skilled": true,
        "regional": true,
        "489)\\\",\\n": true,
        "protection": true,
        "or": true,
        "safe": true,
        "haven": true,
        "enterprise": true,
        "doctoral": true,
        "student)\\\",\\n": true,
        "hours": true,
        "student": true,
        "visa,": true,
        "retirement": true,
        "require": true,
        "sponsorship": true,
        "for": true,
        "new": true,
        "employer": true,
        "482,": true,
        "457)\\\",\\n": true,
        "]\\n": true,
        "\\\"seekAnzPublicTest:applicationQuestionnaireComponent:rrv2:yn5Hrb4iL9MCGkZNLVe4BCedX2sdyZ\\\"\\n": true,
        "\\\"Do": true,
        "you": true,
        "own": true,
        "regular": true,
        "access": true,
        "car?\\\",\\n": true,
        "\\\"Yes\\\",\\n": true,
        "\\\"No\\\",\\n": true,
        "\\\"seekAnzPublicTest:applicationQuestionnaireComponent:rrv2:yn5Hrb4iL9MCGkZNLVe4BCf8QCx8WB\\\"\\n": true,
        "\\\"What": true,
        "do": true,
        "like": true,
        "most": true,
        "about": true,
        "GraphQL?\\\",\\n": true,
        "\\\"FreeText\\\",\\n": true,
        "[]\\n": true,
        "\\\"seekAnzPublicTest:applicationQuestionnaireComponent:rrv2:yn5Hrb4iL9MCGkZNLVe4BCfdHP2d2o\\\"\\n": true,
        "\\\"PrivacyConsent\\\",\\n": true,
        "\\\"descriptionHtml\\\":": true,
        "agree": true,
        "our": true,
        "privacy": true,
        "policy?\\\",\\n": true,
        "\\\"privacyPolicyUrl\\\":": true,
        "\\\"url\\\":": true,
        "\\\"https://www.seek.com.au/privacy/\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":525,\"column\":1,\"offset\":17197},\"end\":{\"line\":625,\"column\":4,\"offset\":20338},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($id: String!) {
  applicationQuestionnaire(id: $id) {
    id {
      value
    }
    components {
      id {
        value
      }
      componentTypeCode
      ... on ApplicationQuestion {
        questionHtml
        responseTypeCode
        value
        responseChoice {
          text
          value
        }
      }
      ... on ApplicationPrivacyConsent {
        descriptionHtml
        privacyPolicyUrl {
          url
        }
      }
    }
  }
}
`}</code></pre>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;